/* BEGIN_COPYRIGHT_HEADER

Copyright Vspry International Limited (c) 2020
All rights reserved.

END_COPYRIGHT_HEADER */

import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import { Icon } from 'vspry-style-components'

import { useLocale } from 'context/localeContext'

import MoreOverlay from 'components/style-elements/MoreOverlay'
import { isNavItem, NavItem, NavSection, NavState } from 'components/navigation/NavDrawer'

type MenuBarProps = {
    allItems: (NavItem | NavSection<NavItem | NavSection> | NavSection)[]
    displayTitles: NavItem['title'][]
    signOut: () => void
    handleClick: (to?: string, onClick?: (state: NavState) => void, fast?: boolean) => void
}

const NavContainer = styled.div`
    position: fixed;
    z-index: 999;
    width: 95vw;
    height: 55px;
    margin: 0px 2.5vw;
    display: flex;
    justify-content: space-around;
    padding: 2px 0px;
    background-color: #fefefe;
    border-radius: 15px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
    top: calc(100dvh - 70px);
    align-items: center;
`

const ItemWrapper = styled.div`
    flex: 1;
    text-align: center;
`

const StyledLink = styled(NavLink)`
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 11px;
    color: #666;
    text-decoration: none;
    padding: 4px 0;
    &:link,
    &:visited,
    &:active,
    &:focus {
        color: #666;
        text-decoration: none;
    }

    &:hover {
        color: ${({ theme }) => theme.accent};
    }

    &.active {
        color: ${({ theme }) => theme.accent};
        font-weight: 600;
    }
`

export default function MobileMenuBar({ allItems, displayTitles, signOut, handleClick }: MenuBarProps) {
    const { translate } = useLocale()
    const [openMore, setOpenMore] = useState(false)

    const moreMenuItem: NavItem = {
        title: `navigation.More`,
        icon: 'ellipsis horizontal',
        onClick: () => {
            setOpenMore(true)
        },
        condition: () => true,
    }

    const displayItems: NavItem[] = displayTitles
        .map((title) => allItems.find((navItem) => navItem.title === title))
        .filter((item) => !!item && isNavItem(item))

    const leftoverItems = allItems.filter((item) => !displayTitles.includes(item.title))

    const finalMenuItems: NavItem[] = leftoverItems.length ? [...displayItems, moreMenuItem] : displayItems

    return (
        <>
            <NavContainer>
                {finalMenuItems.map(({ title, to, icon, onClick }) => (
                    <ItemWrapper>
                        <StyledLink
                            to={to ?? '/undefined'}
                            onClick={(e) => {
                                e.preventDefault()
                                handleClick(to, onClick, true)
                            }}
                        >
                            <Icon name={icon} size='large' />{' '}
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                {translate(title as Parameters<typeof translate>[0]) || title}
                            </span>
                        </StyledLink>
                    </ItemWrapper>
                ))}
            </NavContainer>
            {openMore && <MoreOverlay items={leftoverItems} handleClick={handleClick} onClose={() => setOpenMore(false)} signOut={signOut} />}
        </>
    )
}
